import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ConnectDashboard } from './connect-dashboard.util';
import { LedgerLineTransaction, LedgerPosting, PatientLedger } from './connect-ledger.util';

/** The practice's MedicAI Connect dashboard and patient ledgers, read from Connect through the clinical backend. */
@Injectable({ providedIn: 'root' })
export class ConnectDashboardService {
  constructor(private http: HttpClient) {}

  get(groupId: string): Observable<ConnectDashboard> {
    return this.read<ConnectDashboard>(`api/connect-dashboard/group/${groupId}`);
  }

  /** A patient's Connect ledger, read-only. `patientId` is the Connect patient. */
  ledger(groupId: string, patientId: string): Observable<PatientLedger> {
    return this.read<PatientLedger>(`api/connect-dashboard/group/${groupId}/patients/${patientId}/ledger`);
  }

  postings(groupId: string, patientId: string, entryId: string): Observable<{ postings: LedgerPosting[] }> {
    return this.read(`api/connect-dashboard/group/${groupId}/patients/${patientId}/ledger/entries/${entryId}/postings`);
  }

  transactions(groupId: string, patientId: string, chargeLineId: string): Observable<{ transactions: LedgerLineTransaction[] }> {
    return this.read(`api/connect-dashboard/group/${groupId}/patients/${patientId}/ledger/charge-lines/${chargeLineId}/transactions`);
  }

  /** Declines what Connect asked for, with the reason. */
  declineRequest(groupId: string, requestId: string, reason: string): Observable<unknown> {
    return this.post(`api/connect-dashboard/group/${groupId}/requests/${requestId}/decline`, { reason });
  }

  /** CURA's draft of a requested letter, from the patient's record here. `patientId` is the patient here. */
  draftRequest(groupId: string, requestId: string, body: { patientId: string; reason?: string; claimNumber?: string }): Observable<{ text: string }> {
    return this.post(`api/connect-dashboard/group/${groupId}/requests/${requestId}/draft`, body);
  }

  /** The accepted letter: filed on the patient's timeline here and sent to Connect. */
  acceptRequest(groupId: string, requestId: string, body: { patientId: string; text: string }): Observable<{ noteId: string }> {
    return this.post(`api/connect-dashboard/group/${groupId}/requests/${requestId}/accept`, body);
  }

  private post<T>(url: string, body: unknown): Observable<T> {
    const token = localStorage.getItem('token');
    return this.http.post<{ data: T }>(url, body, { headers: { Authorization: `Bearer ${token}` } }).pipe(map((res) => res.data));
  }

  private read<T>(url: string): Observable<T> {
    const token = localStorage.getItem('token');
    return this.http.get<{ data: T }>(url, { headers: { Authorization: `Bearer ${token}` } }).pipe(map((res) => res.data));
  }
}
